import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useRegisterSW } from "virtual:pwa-register/react";
import Snackbar from "@mui/material/Snackbar";
import { useTranslation } from "i18n-fe";
import { shouldAutoApplyUpdate } from "../util/pwaUpdatePolicy";
import { retryPendingChunkRecovery } from "../util/chunkLoadRecovery";

// #295, revised #1920 — surfaces the vite-plugin-pwa "new version available"
// state. registerType is "prompt", so an updated service worker installs but
// waits; updateServiceWorker(true) activates it and reloads the page.
//
// #1920: a Dismiss button meant a scribe who dismissed (or never saw) this
// snackbar kept running an old, precached shell indefinitely — one whose
// bundle hashes a later deploy had already removed from the CDN, surfacing
// as "Failed to fetch" (bug #183). There is no Dismiss any more: the moment
// an update is waiting, it is applied automatically on a route the explicit
// safe-list in `pwaUpdatePolicy.js` certifies (`shouldAutoApplyUpdate`,
// kiosk mid-ride/mid-cell-entry safety); everywhere else (the scoring sheet,
// the class grid, the OIDC callback) the snackbar becomes a non-dismissible,
// button-less notice — no escape hatch, matching the scribe kiosk doctrine —
// and the effect re-fires on every route change so the update applies the
// moment the scribe reaches a safe route. Applying it there is no different
// from the full page reload sign-out already performs.
//
// #2170: this is also the retry point for a chunk-load recovery
// (chunkLoadRecovery.js) deferred because it arrived on an unsafe route —
// same "re-evaluate on every route change" mechanism, reused rather than
// duplicated.
export default function ReloadPrompt() {
  const { t } = useTranslation("score");
  const location = useLocation();
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW();

  useEffect(() => {
    if (needRefresh && shouldAutoApplyUpdate(location.pathname)) {
      updateServiceWorker(true);
    }
    retryPendingChunkRecovery(location.pathname);
  }, [needRefresh, location.pathname, updateServiceWorker]);

  const waitingOnScoringSheet = needRefresh && !shouldAutoApplyUpdate(location.pathname);

  return (
    <Snackbar
      open={waitingOnScoringSheet}
      message={t("pwa.newVersionPending")}
      data-testid="pwa-reload-prompt"
    />
  );
}
